import type { ComponentProps } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from '@/lib/utils.js';

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export function SelectTrigger({
  className,
  size = 'default',
  children,
  ...props
}: ComponentProps<typeof SelectPrimitive.Trigger> & { size?: 'sm' | 'default' }) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        'flex w-fit items-center justify-between gap-(--space-2)',
        'rounded-(--radius-md) border border-input bg-card text-ink',
        'transition-[border-color,background-color] duration-(--duration-fast) ease-(--ease-out-expo)',
        'hover:bg-surface-hover',
        'data-[size=default]:h-(--control-height) data-[size=default]:px-(--space-3) data-[size=default]:text-[length:var(--text-body)]',
        'data-[size=sm]:h-(--control-height-sm) data-[size=sm]:px-(--space-2) data-[size=sm]:text-[length:var(--text-small)]',
        'data-[placeholder]:text-ink-subtle',
        'disabled:cursor-not-allowed disabled:bg-muted disabled:text-ink-subtle',
        'aria-invalid:border-danger',
        '*:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-(--space-2)',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent({
  className,
  children,
  position = 'popper',
  ...props
}: ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        position={position}
        className={cn(
          'relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] overflow-y-auto',
          'rounded-(--radius-md) border border-rule bg-popover text-popover-foreground shadow-(--shadow-overlay)',
          'duration-(--duration-fast) ease-(--ease-out-expo)',
          'origin-(--radix-select-content-transform-origin)',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-(--space-1) data-[side=top]:-translate-y-(--space-1)',
          className,
        )}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-(--space-1)',
            position === 'popper' && 'w-full min-w-(--radix-select-trigger-width) scroll-my-(--space-1)',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export function SelectLabel({ className, ...props }: ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn(
        'px-(--space-2) py-(--space-1) text-[length:var(--text-micro)] tracking-wide uppercase text-ink-subtle',
        className,
      )}
      {...props}
    />
  );
}

export function SelectItem({
  className,
  children,
  ...props
}: ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'relative flex w-full cursor-default items-center gap-(--space-2)',
        'rounded-(--radius-sm) py-(--space-1) pr-(--space-2) pl-(--space-6)',
        'text-[length:var(--text-small)] text-ink select-none',
        'focus:bg-surface-hover focus:text-ink',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-70',
        "[&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="absolute left-(--space-2) grid size-4 place-items-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-3.5 text-accent" aria-hidden="true" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export function SelectSeparator({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('-mx-(--space-1) my-(--space-1) h-px bg-rule', className)}
      {...props}
    />
  );
}

export function SelectScrollUpButton({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-(--space-1)', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4 text-ink-subtle" aria-hidden="true" />
    </SelectPrimitive.ScrollUpButton>
  );
}

export function SelectScrollDownButton({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-(--space-1)', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4 text-ink-subtle" aria-hidden="true" />
    </SelectPrimitive.ScrollDownButton>
  );
}
